import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { formatMoney, fromMajor } from '@/lib/money'
import { cn } from '@/lib/cn'
import { ESIM_DESTINATIONS, ESIM_PLANS } from './esim-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'
import { BRAND } from '@/brand/brand'

export function EsimCheckoutScreen() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [planId, setPlanId] = useState(params.get('plan') ?? '5gb')
  const [destination, setDestination] = useState(params.get('to') ?? ESIM_DESTINATIONS[0])
  const [starts, setStarts] = useState('arrival')

  const plan = ESIM_PLANS.find((item) => item.id === planId) ?? ESIM_PLANS[1]!
  const total = useMemo(() => fromMajor(plan.priceMajor, 'USD'), [plan])

  return (
    <>
      <PageHeader
        title="Buy a data plan"
        subtitle="Charged to your US Dollar balance. No roaming bill, no contract, nothing to cancel."
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to="/esim">
            Back to eSIM
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Stack gap={5}>
              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  Where are you going?
                </Text>
                <Stack direction="row" gap={2} wrap>
                  {ESIM_DESTINATIONS.map((item) => (
                    <Button
                      key={item}
                      variant={item === destination ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => setDestination(item)}
                    >
                      {item}
                    </Button>
                  ))}
                </Stack>
              </Stack>

              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  How much data?
                </Text>
                <Stack gap={2}>
                  {ESIM_PLANS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={cn(styles.corridor, item.id === planId && styles.corridorActive)}
                      onClick={() => setPlanId(item.id)}
                      aria-pressed={item.id === planId}
                    >
                      <Icon name="sim_card" size={20} />
                      <Stack grow gap={0} align="start">
                        <Text variant="caption" weight="semibold">
                          {item.size} · {item.days}
                        </Text>
                        <Text variant="eyebrow" tone="secondary">
                          {item.body}
                        </Text>
                      </Stack>
                      <Text variant="caption" weight="semibold" numeric>
                        {item.price}
                      </Text>
                    </button>
                  ))}
                </Stack>
              </Stack>

              <Stack gap={3}>
                <Text variant="caption" weight="semibold">
                  When should it start?
                </Text>
                <Stack direction="row" gap={2} wrap>
                  {[
                    { value: 'arrival', label: 'When I land' },
                    { value: 'now', label: 'Right away' },
                  ].map(({ value, label }) => (
                    <Button
                      key={value}
                      variant={starts === value ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => setStarts(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="caption" weight="semibold" tone="onDark">
                Your plan
              </Text>
              <Pill tone="accent">{plan.size}</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-4">
              {formatMoney(total)}
            </Text>

            <div className={checkout.rule} />

            {[
              ['Destination', destination ?? ''],
              ['Data', `${plan.size} · ${plan.days}`],
              ['Starts', starts === 'arrival' ? 'When you land' : 'Right away'],
              ['Activation fee', 'None'],
              ['Paid from', `${BRAND.name} US Dollar balance`],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text variant="caption" weight="semibold" tone={index === 3 ? 'accent' : 'onDark'}>
                  {value}
                </Text>
              </div>
            ))}

            <Card.Footer>
              <Button
                size="lg"
                block
                iconStart="lock"
                onClick={() => navigate(`/esim/done?plan=${plan.id}`, { replace: true })}
              >
                Pay {formatMoney(total)}
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="info" size={14} />
                <Text variant="eyebrow" tone="onDarkMuted">
                  You get the QR code straight after paying. Install it before you fly.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
